// utility function copied from /rovo-extension/packages/extension-frontend/src/utils/getProductKey.ts

const SPECIAL_HOST_PRODUCT_KEYS: Record<string, string> = {
	'calendar.google.com': 'google-calendar',
	'drive.google.com': 'GoogleDrive',
	'docs.google.com': 'GoogleDrive',
	'mail.google.com': 'gmail',
	'teams.microsoft.com': 'Microsoft',
	'outlook.com': 'outlook-mail',
	'outlook.office.com': 'outlook-mail',
	'home.atlassian.com': 'townsquare',
	'xd.adobe.com': 'adobexd',
};

const ATLASSIAN_DOMAIN_SUFFIXES = ['.atlassian.net', '.atlassian.cloud', '.jira-dev.com'];

const ATLASSIAN_PATH_PRODUCT_KEYS: Record<string, string> = {
	'/wiki': 'Confluence',
	'/browse': 'Jira',
	'/jira': 'Jira',
	'/compass': 'Compass',
	'/focus': 'Mercury',
};

const DOMAIN_SUFFIX_OVERRIDES: Record<string, string> = {
	'.powerbi.com': 'power-bi',
	'.azure.com': 'azure-devops',
	'.adobesign.com': 'adobe-sign',
};

const VALID_PRODUCT_KEYS: Set<string> = new Set([
	'bitbucket',
	'compass',
	'confluence',
	'jira',
	'jira-product-discovery',
	'jira-servicedesk',
	'trello',
	'mercury',
	'slack',
	'figma',
	'drive',
	'sharepoint',
	'onedrive',
	'dropbox',
	'github',
	'gitlab',
	'google-calendar',
	'gmail',
	'outlook-mail',
	'outlook-calendar',
	'teams',
	'zendesk',
	'airtable',
	'asana',
	'smartsheet',
	'notion',
	'miro',
	'docusign',
	'lucid',
	'monday',
	'azure-devops',
	'box',
	'zeplin',
	'servicenow',
	'power-bi',
	'clickup',
	'amplitude',
	'stripe',
	'adobexd',
	'aha',
	'mural',
	'webex',
	'todoist',
	'pagerduty',
	'pipedrive',
	'hubspot',
	'dovetail',
	'launchdarkly',
	'salesforce',
	'sentry',
	'loom',
	'adobe-sign',
]);

function getProductKeyFromDomain(hostname: string): string {
	// extract domain suffix and check if it maps to a valid product key
	const domainParts = hostname.split('.');
	if (domainParts.length >= 2) {
		const suffix = '.' + domainParts.slice(-2).join('.');

		if (DOMAIN_SUFFIX_OVERRIDES[suffix]) {
			return DOMAIN_SUFFIX_OVERRIDES[suffix];
		}

		// extract the main domain part (e.g. "zendesk" from "zendesk.com")
		const mainDomain = domainParts[domainParts.length - 2];

		// check if the main domain is a valid product key
		if (VALID_PRODUCT_KEYS.has(mainDomain)) {
			return mainDomain
				.split('-')
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
				.join(' ');
		}

		return 'Unknown';
	}

	return 'Unknown';
}

// if finding a valid key fails, fallback on returning 'Unknown'
// to fallback to be included as 1P product in products1PList in analytics-helpers.tsx
export function getProductKey(url: string): string {
	const { hostname, pathname } = new URL(url);

	// check for special hostname mappings (e.g. calendar.google.com → google-calendar)
	if (SPECIAL_HOST_PRODUCT_KEYS[hostname]) {
		return SPECIAL_HOST_PRODUCT_KEYS[hostname];
	}

	// check if it's an Atlassian domain (e.g. company.atlassian.net)
	const isAtlassianDomain =
		ATLASSIAN_DOMAIN_SUFFIXES.some((suffix) => hostname.endsWith(suffix)) ||
		hostname.includes('.atlassian.');

	if (isAtlassianDomain) {
		// for Atlassian domains, identify product based on path patterns (e.g. /wiki → confluence)
		for (const [pathPattern, productKey] of Object.entries(ATLASSIAN_PATH_PRODUCT_KEYS)) {
			if (pathname.includes(pathPattern)) {
				return productKey;
			}
		}

		return 'Unknown';
	}

	// for other domains, extract product key from domain name (e.g. zendesk.com → zendesk)
	return getProductKeyFromDomain(hostname);
}
